import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth as adminAuth } from '../admin-auth/adminAppAuth';
import {
  signInWithEmailAndPassword,
  signOut as signOutSession,
  onAuthStateChanged,
} from '../admin-auth/adminAuthApi';
import { fetchAdminMe, markAdminChallenge } from '../api/adminMeApi';
import {
  consumeAdminSmsCode,
  requestAdminMagicLink,
  requestAdminSmsCode,
} from '../magic-link-auth/services/adminMagicLinkApi';
import { impersonateUser as startImpersonation, endImpersonation as endImpersonationSession } from '../impersonation/services/impersonationService';
import { isTransientRequestError } from '../../api/network/transientError';

function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isUnauthorizedAdminError(error) {
  const status = error?.status || error?.body?.status;
  return status === 401 || status === 403 || String(error?.message || '').includes('Unauthorized');
}

async function fetchAdminMeWithRetry() {
  const startedAt = Date.now();
  let delay = 400;
  let lastError;

  while (Date.now() - startedAt < 20000) {
    try {
      return await fetchAdminMe();
    } catch (error) {
      lastError = error;
      if (isUnauthorizedAdminError(error) || !isTransientRequestError(error)) {
        throw error;
      }
      await wait(delay);
      delay = Math.min(delay * 2, 4000);
    }
  }

  if (lastError) throw lastError;
  return fetchAdminMe();
}

const AdminAuthContext = createContext();

const MAGIC_LINK_PENDING_STARTED_AT_KEY = 'admin_magic_link_pending_started_at';
const MAGIC_LINK_PENDING_CHALLENGE_ID_KEY = 'admin_magic_link_pending_challenge_id';
const MAGIC_LINK_PENDING_METHOD_KEY = 'admin_magic_link_pending_method';
const MAGIC_LINK_PENDING_DESTINATION_KEY = 'admin_magic_link_pending_destination';

function readPendingStartedAt() {
  try {
    const v = sessionStorage.getItem(MAGIC_LINK_PENDING_STARTED_AT_KEY);
    if (!v) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writePendingStartedAt(ts) {
  try {
    if (!ts) {
      sessionStorage.removeItem(MAGIC_LINK_PENDING_STARTED_AT_KEY);
      sessionStorage.removeItem(MAGIC_LINK_PENDING_METHOD_KEY);
      sessionStorage.removeItem(MAGIC_LINK_PENDING_DESTINATION_KEY);
    } else {
      sessionStorage.setItem(MAGIC_LINK_PENDING_STARTED_AT_KEY, String(ts));
    }
  } catch {
    // ignore storage failures
  }
}

function readPendingChallengeId() {
  try {
    const v = sessionStorage.getItem(MAGIC_LINK_PENDING_CHALLENGE_ID_KEY);
    return v ? String(v) : null;
  } catch {
    return null;
  }
}

function writePendingChallengeId(v) {
  try {
    if (!v) {
      sessionStorage.removeItem(MAGIC_LINK_PENDING_CHALLENGE_ID_KEY);
    } else {
      sessionStorage.setItem(MAGIC_LINK_PENDING_CHALLENGE_ID_KEY, String(v));
    }
  } catch {
    // ignore
  }
}

function readPendingMethod() {
  try {
    const v = String(sessionStorage.getItem(MAGIC_LINK_PENDING_METHOD_KEY) || '').trim().toLowerCase();
    if (v === 'sms' || v === 'email') return v;
    return '';
  } catch {
    return '';
  }
}

function writePendingMethod(v) {
  try {
    if (!v) {
      sessionStorage.removeItem(MAGIC_LINK_PENDING_METHOD_KEY);
    } else {
      sessionStorage.setItem(MAGIC_LINK_PENDING_METHOD_KEY, v === 'sms' ? 'sms' : 'email');
    }
  } catch {
    // ignore
  }
}

function readPendingDestination() {
  try {
    return String(sessionStorage.getItem(MAGIC_LINK_PENDING_DESTINATION_KEY) || '');
  } catch {
    return '';
  }
}

function writePendingDestination(v) {
  try {
    if (!v) {
      sessionStorage.removeItem(MAGIC_LINK_PENDING_DESTINATION_KEY);
    } else {
      sessionStorage.setItem(MAGIC_LINK_PENDING_DESTINATION_KEY, String(v));
    }
  } catch {
    // ignore
  }
}

function generateChallengeId() {
  // Opaque client-generated id; used only to match the click to the login attempt.
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

const TWO_FA_STORAGE_KEY = 'admin_2fa_verified_at';

function readTwoFaVerifiedAt() {
  try {
    const n = Number(localStorage.getItem(TWO_FA_STORAGE_KEY) || '');
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeTwoFaVerifiedAt() {
  try {
    localStorage.setItem(TWO_FA_STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

function adminStateFromProfile(user, profile) {
  return {
    uid: user.uid,
    email: user.email,
    ...profile,
    getIdToken: () => user.getIdToken(true),
  };
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [authUser, setAuthUser] = useState(null);
  const [originalAdminData, setOriginalAdminData] = useState(null);
  const [pendingMagicLinkStartedAt, setPendingMagicLinkStartedAt] = useState(() => readPendingStartedAt());
  const [pendingMagicLinkLogin, setPendingMagicLinkLogin] = useState(() => Boolean(readPendingStartedAt()));
  const signingOutRef = useRef(false);
  const originalAdminDataRef = useRef(null);
  const pendingMagicLinkLoginRef = useRef(false);
  const adminDocUnsubRef = useRef(null);

  useEffect(() => {
    originalAdminDataRef.current = originalAdminData;
  }, [originalAdminData]);

  useEffect(() => {
    pendingMagicLinkLoginRef.current = pendingMagicLinkLogin;
  }, [pendingMagicLinkLogin]);

  useEffect(() => {
    if (!adminAuth) {
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(adminAuth, async (user) => {
      try {
        // During sign-out, avoid any Firestore reads that can race with auth teardown.
        if (signingOutRef.current) {
          if (!user) {
            setAdmin(null);
            setIsImpersonating(false);
            setAuthUser(null);
            setOriginalAdminData(null);
            setPendingMagicLinkLogin(false);
            setPendingMagicLinkStartedAt(null);
            writePendingStartedAt(null);
            writePendingChallengeId(null);
          }
          setLoading(false);
          return;
        }

        if (user) {
          console.log('Auth state changed - User:', user.uid);
          setAuthUser({ uid: user.uid, email: user.email || null });

          // IMPORTANT: enforce pending magic-link lock using sessionStorage too,
          // so it can't be bypassed by refresh or React state races.
          const pendingStartedAtFromStorage = readPendingStartedAt();
          const pendingChallengeIdFromStorage = readPendingChallengeId();
          const pendingActive = Boolean(pendingStartedAtFromStorage);
          
          // Get the user's token result to check custom claims
          const tokenResult = await user.getIdTokenResult();
          console.log('Token claims:', tokenResult.claims);

          // Check if this is an impersonated session
          if (tokenResult.claims.impersonating) {
            console.log('Setting impersonated user state');
            setIsImpersonating(true);
            setAdmin({
              uid: user.uid,
              email: user.email,
              isImpersonated: true,
              impersonatedBy: tokenResult.claims.impersonatedBy,
              originalAdmin: originalAdminDataRef.current,
              getIdToken: () => user.getIdToken(true)
            });
            setLoading(false);
            return;
          }

          // If the user is in the middle of the "password -> email link" flow,
          // only allow a real session once 2FA verification is recorded.

          let profile;
          try {
            profile = await fetchAdminMeWithRetry();
          } catch (e) {
            if (isUnauthorizedAdminError(e)) {
              setAdmin(null);
              setIsImpersonating(false);
              setLoading(false);
              return;
            }
            throw e;
          }

          if (profile?.uid || profile?.id) {
            console.log('Admin document found');

            if (pendingMagicLinkLoginRef.current || pendingActive) {
              const verifiedChallengeId = profile.lastMagicLinkChallengeId || null;
              const expectedChallengeId = pendingChallengeIdFromStorage || null;
              const verifiedByTab = readTwoFaVerifiedAt() >= Number(pendingStartedAtFromStorage || 0);

              const isVerified =
                (expectedChallengeId && verifiedChallengeId && verifiedChallengeId === expectedChallengeId) ||
                verifiedByTab;
              if (!isVerified) {
                // Stay "locked" even though Auth has a user session.
                setAdmin(null);
                setIsImpersonating(false);
                setLoading(false);
                return;
              }

              if (expectedChallengeId && verifiedChallengeId !== expectedChallengeId) {
                try {
                  await markAdminChallenge(expectedChallengeId);
                } catch {
                  // ignore; local tab signal already verified
                }
              }

              setPendingMagicLinkLogin(false);
              setPendingMagicLinkStartedAt(null);
              writePendingStartedAt(null);
              writePendingChallengeId(null);
            }

            const adminData = adminStateFromProfile(user, profile);
            setAdmin(adminData);
            setOriginalAdminData(adminData);
            setIsImpersonating(false);
            setError(null);
          } else {
            console.log('No admin document found, signing out');
            await signOutSession(adminAuth);
            setAdmin(null);
            setIsImpersonating(false);
          }
        } else {
          console.log('No user signed in');
          setAdmin(null);
          setIsImpersonating(false);
          setAuthUser(null);
          setOriginalAdminData(null);
          setPendingMagicLinkLogin(false);
          setPendingMagicLinkStartedAt(null);
          writePendingStartedAt(null);
          writePendingChallengeId(null);
        }
      } catch (err) {
        console.error('Auth state change error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authUser || admin || !error || pendingMagicLinkLogin) return undefined;

    let cancelled = false;
    const tick = async () => {
      try {
        const profile = await fetchAdminMe();
        const current = adminAuth.currentUser;
        if (cancelled || !current || !(profile?.uid || profile?.id)) return;
        setAdmin(adminStateFromProfile(current, profile));
        setOriginalAdminData(adminStateFromProfile(current, profile));
        setError(null);
      } catch {
        // keep polling while the API is coming back up
      }
    };

    const interval = setInterval(() => {
      void tick();
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [admin, authUser, error, pendingMagicLinkLogin]);

  // While waiting for the email link click, poll the admin record and a localStorage
  // signal from the magic-link tab so we can unlock without Firestore.
  useEffect(() => {
    if (adminDocUnsubRef.current) {
      try {
        adminDocUnsubRef.current();
      } catch {
        // ignore
      }
      adminDocUnsubRef.current = null;
    }

    const expectedChallengeId = readPendingChallengeId();
    const pendingStartedAt = readPendingStartedAt();
    const pendingActive = Boolean(pendingStartedAt) && Boolean(expectedChallengeId);
    if (!authUser?.uid || !pendingActive) return;

    let cancelled = false;

    const unlockIfReady = async (profile) => {
      if (cancelled) return;
      const verifiedChallengeId = profile?.lastMagicLinkChallengeId || null;
      const verifiedByTab = readTwoFaVerifiedAt() >= Number(pendingStartedAt || 0);
      const isVerified =
        (expectedChallengeId && verifiedChallengeId && verifiedChallengeId === expectedChallengeId) ||
        verifiedByTab;
      if (!isVerified) return;

      if (expectedChallengeId && verifiedChallengeId !== expectedChallengeId) {
        try {
          await markAdminChallenge(expectedChallengeId);
        } catch {
          // ignore
        }
      }

      setPendingMagicLinkLogin(false);
      setPendingMagicLinkStartedAt(null);
      writePendingStartedAt(null);
      writePendingChallengeId(null);
      setAdmin({
        uid: authUser.uid,
        email: authUser.email,
        ...profile,
        getIdToken: () => adminAuth.currentUser?.getIdToken(true),
      });
    };

    const tick = async () => {
      try {
        const profile = await fetchAdminMe();
        await unlockIfReady(profile);
      } catch {
        await unlockIfReady(null);
      }
    };

    const onStorage = (event) => {
      if (event.key === TWO_FA_STORAGE_KEY) {
        void tick();
      }
    };

    void tick();
    const interval = setInterval(() => {
      void tick();
    }, 2000);
    window.addEventListener('storage', onStorage);

    adminDocUnsubRef.current = () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('storage', onStorage);
    };
    return () => {
      try {
        adminDocUnsubRef.current?.();
      } catch {
        // ignore
      }
      adminDocUnsubRef.current = null;
    };
  }, [authUser?.uid, authUser?.email, pendingMagicLinkLogin]);

  const signIn = async (email, password) => {
    try {
      setError(null);
      const userCredential = await signInWithEmailAndPassword(adminAuth, email, password);
      console.log('Sign in successful for:', userCredential.user.uid);

      try {
        await fetchAdminMe();
      } catch (e) {
        await signOutSession(adminAuth);
        throw new Error(e?.message || 'Unauthorized access. Admin privileges required.');
      }

      return userCredential;
    } catch (err) {
      console.error('Sign in error:', err);
      setError(err.message);
      throw err;
    }
  };

  const signInWithEmailLink = async (email, password) => {
    try {
      setError(null);

      // If we already started a magic-link attempt in this tab/session, don't spam new emails.
      const existingChallenge = readPendingChallengeId();
      const alreadyPending = Boolean(existingChallenge) && Boolean(readPendingStartedAt());
      if (alreadyPending) {
        setPendingMagicLinkLogin(true);
        return {
          magicLinkSent: true,
          alreadyPending: true,
          secondFactorMethod: readPendingMethod(),
          maskedDestination: readPendingDestination(),
        };
      }

      // IMPORTANT: pre-lock the session BEFORE password sign-in to avoid races where onAuthStateChanged
      // sets `admin` immediately after password auth. We'll clear this lock if the account setting says
      // magic-link is not required.
      const startedAt = Date.now();
      setPendingMagicLinkLogin(true);
      setPendingMagicLinkStartedAt(startedAt);
      writePendingStartedAt(startedAt);
      const challengeId = generateChallengeId();
      writePendingChallengeId(challengeId);

      // Verify credentials (first factor)
      let userCredential;
      try {
        userCredential = await signInWithEmailAndPassword(adminAuth, email, password);
        console.log('Password verified for:', userCredential.user.uid);
      } catch (authError) {
        // Clean up pending state before re-throwing credential errors
        setPendingMagicLinkLogin(false);
        setPendingMagicLinkStartedAt(null);
        writePendingStartedAt(null);
        writePendingChallengeId(null);
        
        // Sign out if user was somehow authenticated (shouldn't happen, but safety check)
        try {
          if (adminAuth.currentUser) {
            await signOutSession(adminAuth);
          }
        } catch {
          // ignore signout errors
        }
        
        throw authError;
      }

      // Decide whether this account requires 2FA.
      // Default is OFF unless explicitly enabled on the admin doc.
      let requireMagicLink = false;
      let twoFactorMethod = 'email';
      try {
        const profile = await fetchAdminMe();
        if (profile?.requireMagicLink === true) {
          requireMagicLink = true;
        }
        if (profile?.twoFactorMethod === 'sms') {
          twoFactorMethod = 'sms';
        }
      } catch {
        requireMagicLink = false;
        twoFactorMethod = 'email';
      }

      if (!requireMagicLink) {
        // Normal password login (no email link required).
        setPendingMagicLinkLogin(false);
        setPendingMagicLinkStartedAt(null);
        writePendingStartedAt(null);
        writePendingChallengeId(null);

        try {
          const profile = await fetchAdminMe();
          setAdmin(adminStateFromProfile(userCredential.user, profile));
        } catch {
          // ignore; route guards will still handle access
        }

        return { passwordOnly: true };
      }

      if (twoFactorMethod === 'sms') {
        writePendingMethod('sms');
        try {
          const smsRes = await requestAdminSmsCode({ email, challengeId });
          const masked = smsRes?.maskedDestination || '';
          writePendingDestination(masked);
          return {
            magicLinkSent: true,
            secondFactorMethod: 'sms',
            maskedDestination: masked || null,
          };
        } catch (e) {
          if (e?.status === 429 || String(e?.code || e?.errorInfo?.code || '').includes('resource-exhausted')) {
            throw new Error(e?.message || 'Please wait before requesting another SMS code.');
          }
          throw e;
        }
      }

      // Require email link (second factor)
      writePendingMethod('email');
      writePendingDestination('');
      await requestAdminMagicLink({ email, challengeId });

      return { magicLinkSent: true, secondFactorMethod: 'email' };
    } catch (err) {
      console.error('Magic link sign-in error:', err);
      
      // Clean up pending state on any error
      setPendingMagicLinkLogin(false);
      setPendingMagicLinkStartedAt(null);
      writePendingStartedAt(null);
      writePendingChallengeId(null);
      
      // Sign out if user was authenticated but error occurred
      // (e.g., if password was correct but magic link sending failed)
      const errorCode = err?.code || '';
      const isCredentialError = errorCode === 'auth/invalid-credential' || 
                                errorCode === 'auth/wrong-password' || 
                                errorCode === 'auth/user-not-found' ||
                                String(err?.message || '').includes('invalid-credential') ||
                                String(err?.message || '').includes('wrong-password') ||
                                String(err?.message || '').includes('user-not-found');
      
      if (isCredentialError || adminAuth.currentUser) {
        try {
          await signOutSession(adminAuth);
        } catch {
          // ignore signout errors
        }
      }
      
      setError(err?.message || String(err));
      throw err;
    }
  };

  const verifyPendingSmsCode = async (code) => {
    const challengeId = readPendingChallengeId();
    if (!challengeId) {
      throw new Error('Missing verification challenge. Please sign in again.');
    }
    const normalizedCode = String(code || '').trim();
    if (!/^\d{6}$/.test(normalizedCode)) {
      throw new Error('Code must be 6 digits');
    }
    await consumeAdminSmsCode({ challengeId, code: normalizedCode });
    writeTwoFaVerifiedAt();
    try {
      await markAdminChallenge(challengeId);
    } catch {
      // localStorage signal still unlocks the pending session
    }
    return { success: true };
  };

  const getPendingSecondFactorInfo = () => {
    return {
      secondFactorMethod: readPendingMethod(),
      maskedDestination: readPendingDestination(),
    };
  };

  const signOut = async () => {
    try {
      signingOutRef.current = true;
      if (adminDocUnsubRef.current) {
        try {
          adminDocUnsubRef.current();
        } catch {
          // ignore
        }
        adminDocUnsubRef.current = null;
      }
      await signOutSession(adminAuth);
      setAdmin(null);
      setIsImpersonating(false);
      setAuthUser(null);
      setPendingMagicLinkLogin(false);
      setPendingMagicLinkStartedAt(null);
      writePendingStartedAt(null);
      writePendingChallengeId(null);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      // Clear on next tick so onAuthStateChanged has a chance to flush `user=null` safely.
      setTimeout(() => {
        signingOutRef.current = false;
      }, 0);
    }
  };

  const impersonateUser = async (userId, companyId) => {
    try {
      setLoading(true);
      const currentUser = adminAuth.currentUser;
      if (!currentUser) {
        throw new Error('No admin user logged in');
      }

      const currentAdminData = { ...admin };
      setOriginalAdminData(currentAdminData);
      setIsImpersonating(true);

      const result = await startImpersonation(userId, companyId);
      if (!result?.token) {
        throw new Error('Failed to get impersonation token');
      }
      return result;
    } catch (err) {
      console.error('Impersonation error:', err);
      setIsImpersonating(false);
      setOriginalAdminData(null);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const endImpersonation = async () => {
    try {
      setLoading(true);
      await endImpersonationSession(admin?.uid);
      const storedAdminData = originalAdminData;
      setIsImpersonating(false);
      setOriginalAdminData(null);
      if (storedAdminData) {
        setAdmin(storedAdminData);
      }
      return { success: true };
    } catch (err) {
      console.error('End impersonation error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    admin,
    authUser,
    loading,
    error,
    signIn,
    signInWithEmailLink,
    verifyPendingSmsCode,
    getPendingSecondFactorInfo,
    signOut,
    impersonateUser,
    endImpersonation,
    isImpersonating,
    setIsImpersonating,
    originalAdminData,
    pendingMagicLinkLogin
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
}; 